import React, { useMemo, useCallback } from "react";
import { ElementSize } from "src/shared/components/CustomisedShimmer";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip, ResponsiveContainer } from "recharts";
import "./index.scss";
import CustomAxisTick from "../CustomAxisTick";
import Shimmer from "src/shared/components/CustomisedShimmer";
import colors from "src/styles/variables.scss";
import { abbreviateNumber, safeGet } from "src/shared/utils";

type ChartProp = {
  isPercentage: boolean;
  isPending: boolean;
  width: number;
  height: number;
  chart: {
    data: Array<IObject<any>>;
    y1Key: string;
    y2Key: string;
    y3Key?: string;
    formatted: any;
  };
};

function adjust(color, amount) {
  return (
    "#" +
    color
      .replace(/^#/, "")
      .replace(/../g, (color) =>
        ("0" + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2)
      )
  );
}

export const CurrentDots = (props) => {
  const { cx, cy, color } = props;
  return cy ? (
    <circle cx={cx} cy={cy} r={3} stroke={adjust(color, 60)} strokeWidth={1} fill={colors.chartBackground} />
  ) : null;
};

export const ActiveDots = (props) => {
  const { cx, cy, color } = props;
  return cy ? <circle cx={cx} cy={cy} r={6} stroke={adjust(color, 60)} strokeWidth={3} fill={color} /> : null;
};

const DEFAULT_COMPARE_NO = 3;

const DeltaValue = ({ value, label }: { value: number | null; label: string }) => {
  if (value === null) return null;
  const colorClass = value >= 0 ? "tooltip-delta-positive" : "tooltip-delta-negative";
  return (
    <div className={colorClass}>
      {value >= 0 ? "+" : ""}
      {value.toFixed(1)}% vs {label}
    </div>
  );
};

type PlanTooltipProps = {
  active?: boolean;
  payload?: any[];
  label?: string;
  allData: Array<IObject<any>>;
  y1Key: string;
  y2Key: string;
  y3Key: string;
};

const PlanTooltip = ({ active, payload, label, allData, y1Key, y2Key, y3Key }: PlanTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  const todayVal = dataPoint[y1Key];
  const sdlwVal = dataPoint[y2Key];
  const planVal = dataPoint[y3Key];

  const currentIdx = allData.findIndex((d) => d.x === dataPoint.x);

  let cumToday = 0;
  let cumSdlw = 0;
  let cumPlan = 0;
  for (let i = 0; i <= currentIdx; i++) {
    cumToday += allData[i][y1Key] || 0;
    cumSdlw += allData[i][y2Key] || 0;
    cumPlan += allData[i][y3Key] || 0;
  }

  const hourlyVsSdlw = sdlwVal ? ((todayVal - sdlwVal) / sdlwVal) * 100 : null;
  const hourlyVsPlan = planVal ? ((todayVal - planVal) / planVal) * 100 : null;
  const cumVsSdlw = cumSdlw ? ((cumToday - cumSdlw) / cumSdlw) * 100 : null;
  const cumVsPlan = cumPlan ? ((cumToday - cumPlan) / cumPlan) * 100 : null;

  return (
    <div className="plan-tooltip">
      <div className="plan-tooltip__time">{label}</div>
      <div className="plan-tooltip__row">
        <span className="plan-tooltip__label">{y2Key}:</span> {abbreviateNumber(sdlwVal ?? 0)}
      </div>
      <div className="plan-tooltip__row">
        <span className="plan-tooltip__label" style={{ color: colors.chartdarkAxis }}>{y1Key}:</span>{" "}
        <span style={{ color: colors.chartdarkAxis }}>{abbreviateNumber(todayVal ?? 0)}</span>
      </div>
      <div className="plan-tooltip__row">
        <span className="plan-tooltip__label" style={{ color: "#00A876" }}>{y3Key}:</span>{" "}
        <span style={{ color: "#00A876" }}>{abbreviateNumber(planVal ?? 0)}</span>
      </div>
      <div className="plan-tooltip__section-label">Hourly</div>
      <DeltaValue value={hourlyVsSdlw} label={y2Key} />
      <DeltaValue value={hourlyVsPlan} label="Plan" />
      <div className="plan-tooltip__section-label">Cumulative</div>
      <DeltaValue value={cumVsSdlw} label={y2Key} />
      <DeltaValue value={cumVsPlan} label="Plan" />
    </div>
  );
};

const CustomLineChart = (props: ChartProp) => {
  const { width, height } = props;
  const barsConfig = useMemo(
    () => [
      {
        height: "100%",
        width: "100%",
        elementSize: ElementSize.LONG,
      },
    ],
    []
  );
  const tooltipFormatter = useCallback((value) => {
    return abbreviateNumber(value);
  }, []);

  return !props.isPending ? (
    <div className="custom-line-container">
      <ResponsiveContainer height="100%">
        <LineChart
          data={safeGet(props, "chart.data", [])}
          margin={{
            top: 20,
            right: 20,
            left: -20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={false} />

          <XAxis dataKey="formatted" tickSize={5} interval="preserveStart" tick={{ fontSize: 10 }} />
          <YAxis tick={<CustomAxisTick isPercentage={props.isPercentage} />} axisLine={false} tickSize={0} />
          {safeGet(props, "chart.y3Key") ? (
            <Tooltip
              content={
                <PlanTooltip
                  allData={safeGet(props, "chart.data", [])}
                  y1Key={safeGet(props, "chart.y1Key", "")}
                  y2Key={safeGet(props, "chart.y2Key", "")}
                  y3Key={safeGet(props, "chart.y3Key", "")}
                />
              }
              wrapperStyle={{ outline: "none" }}
            />
          ) : (
            <Tooltip
              wrapperStyle={{
                backgroundColor: "black",
                opacity: 0.7,
                maxWidth: "50vw",
                overflow: "hidden",
              }}
              formatter={tooltipFormatter}
            />
          )}
          <Legend />

          <Line
            type="linear"
            fontFamily="sans-serif"
            dataKey={safeGet(props, "chart.y2Key")}
            stroke={colors.chartlightAxis}
            dot={false}
            activeDot={false}
            strokeWidth={2}
            connectNulls
          />

          <Line
            type="linear"
            fontFamily="sans-serif"
            dataKey={safeGet(props, "chart.y1Key")}
            stroke={colors.chartdarkAxis}
            dot={<CurrentDots color={colors.chartdarkAxis} />}
            activeDot={<ActiveDots color={colors.chartdarkAxis} />}
            strokeWidth={3}
            connectNulls
          />

          {/* Plan Orders line - only rendered if y3Key exists */}
          {safeGet(props, "chart.y3Key") && (
            <Line
              type="linear"
              fontFamily="sans-serif"
              dataKey={safeGet(props, "chart.y3Key")}
              stroke="#00A876"
              dot={false}
              activeDot={<ActiveDots color="#00A876" />}
              strokeWidth={2}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  ) : (
    <div className="custom-line-container">
      <Shimmer className="Shimmer" barsConfig={barsConfig} />
    </div>
  );
};

export default CustomLineChart;
